import React, {useEffect, useState} from "react";
import Navbar from "../../Components/Navigation/Navbar";
import {Button, Card, Container} from "react-bootstrap";
import { getAllAlbums, getImagesByAlbum } from "../../api/requests";
import { useNavigate } from "react-router-dom";

function GalleryPageSeen() {
    const [albums, setAlbums] = useState([]);
    const [imagesByAlbum, setImagesByAlbum] = useState({});
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAlbumsAndImages = async () => {
            try {
                const publicAlbums = await getAllAlbums();
                setAlbums(publicAlbums);

                const imagesObj = {};
                await Promise.all(
                    publicAlbums.map(async (album) => {
                        const images = await getImagesByAlbum(album.id);
                        imagesObj[album.id] = images || [];
                    })
                );
                setImagesByAlbum(imagesObj);
            } catch (err) {
                setAlbums([]);
                setImagesByAlbum({});
            } finally {
                setLoading(false);
            }
        };
        fetchAlbumsAndImages();
    }, []);

    const openAlbum = (albumId) => {
        navigate(`/album/${albumId}`);
    };

    // Masonry CSS
    const masonryStyle = {
        columnCount: 1,
        columnGap: "16px",
        maxWidth: 1200,
        margin: "0 auto"
    };
    if (window.innerWidth >= 1024) masonryStyle.columnCount = 3;
    else if (window.innerWidth >= 640) masonryStyle.columnCount = 2;

    return (
        <>
            <Navbar/>
            <Container>
                <Card>
                    <Card.Header>
                        <Card.Title>Alben</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {loading ? (
                            <p>Lade Alben...</p>
                        ) : albums.length === 0 ? (
                            <p>Keine öffentlichen Alben gefunden.</p>
                        ) : (
                            albums.map(album => {
                                const images = imagesByAlbum[album.id] || [];
                                const previewImages = images.slice(0, 6);
                                const remainingCount = Math.max(0, images.length - previewImages.length);
                                return (
                                    <section
                                        key={album.id}
                                        aria-label={`Fotogalerie Album ${album.title}`}
                                        style={{marginBottom: "2rem", cursor: "pointer"}}
                                        onClick={() => openAlbum(album.id)}
                                    >
                                        <h4 style={{ marginBottom: 4 }}>{album.title}</h4>
                                        <p className="plain-text">{album.description}</p>
                                        {previewImages.length === 0 ? (
                                            <p>Keine öffentlichen Bilder in diesem Album.</p>
                                        ) : (
                                            <div style={masonryStyle}>
                                                {previewImages.map((image, idx) => {
                                                    const isLastPreview = idx === previewImages.length - 1;
                                                    const showMoreOverlay = remainingCount > 0 && isLastPreview;

                                                    return (
                                                    <figure
                                                        key={image.id}
                                                        tabIndex={0}
                                                        style={{
                                                            breakInside: "avoid",
                                                            margin: 0,
                                                            marginBottom: 16,
                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                                            borderRadius: 12,
                                                            overflow: "hidden",
                                                            background: "#fff",
                                                            position: "relative"
                                                        }}
                                                    >
                                                        <a
                                                            href={`/album/${album.id}`}
                                                            tabIndex={-1}
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                openAlbum(album.id);
                                                            }}
                                                            style={{display: "block"}}
                                                        >
                                                            <img
                                                                src={image.image}
                                                                alt={image.title || `Foto aus Album ${album.title}`}
                                                                loading="lazy"
                                                                decoding="async"
                                                                width="100%"
                                                                style={{
                                                                    width: "100%",
                                                                    aspectRatio: "4/3",
                                                                    objectFit: "cover",
                                                                    display: "block"
                                                                }}
                                                            />
                                                        </a>

                                                        {showMoreOverlay && (
                                                            <div
                                                                aria-label={`${remainingCount} weitere Fotos in diesem Album`}
                                                                style={{
                                                                    position: "absolute",
                                                                    inset: 0,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    background: "rgba(255,255,255,0.62)",
                                                                    color: "rgba(0,0,0,0.78)",
                                                                    fontWeight: 800,
                                                                    fontSize: "clamp(22px, 6vw, 44px)",
                                                                    letterSpacing: 0.5,
                                                                    pointerEvents: "none"
                                                                }}
                                                            >
                                                                +{remainingCount}
                                                            </div>
                                                        )}
                                                        <figcaption
                                                            style={{
                                                                position: "absolute",
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                padding: "16px 12px 8px 12px",
                                                                background: "linear-gradient(0deg,rgba(0,0,0,0.7) 70%,rgba(0,0,0,0.0) 100%)",
                                                                color: "#fff",
                                                                fontSize: 15,
                                                                minHeight: 48,
                                                                opacity: 0.95,
                                                                transition: "opacity 0.2s",
                                                                pointerEvents: "none"
                                                            }}
                                                            className="gallery-caption"
                                                        >
                                                            <div style={{fontWeight: 600, fontSize: 17}}>
                                                                {image.title || "Ohne Titel"}
                                                            </div>
                                                            {image.photographer && (
                                                                <div style={{fontSize: 13, opacity: 0.85}}>
                                                                    © {image.photographer}
                                                                </div>
                                                            )}
                                                            {image.description && (
                                                                <div style={{fontSize: 13, marginTop: 2, opacity: 0.85}}>
                                                                    {image.description.substring(0, 120)}
                                                                </div>
                                                            )}
                                                        </figcaption>
                                                        <style>{`
                                                            figure:focus-visible .gallery-caption,
                                                            figure:hover .gallery-caption {
                                                                opacity: 1 !important;
                                                                pointer-events: auto;
                                                            }
                                                            @media (hover: none) {
                                                                .gallery-caption {
                                                                    opacity: 1 !important;
                                                                }
                                                            }
                                                        `}</style>
                                                    </figure>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </section>
                                );
                            })
                        )}
                    </Card.Body>
                </Card>
            </Container>
            <Card className="impressum">
                <Button variant="Link" href="/kontakt">Impressum</Button>
            </Card>
        </>
    );
}

export default GalleryPageSeen;
